<template>
  <div class="text-center" :class="{ 'safety-assistant': safetyAssistant }">
    <h3 class="heading mt-2 mb-2">
      <v-icon color="black" class="mr-2">{{
        type === 'phone' ? 'mdi-cellphone' : 'mdi-email'
      }}</v-icon>
      {{ $t('enterCode.checkYour', { type: $t('common.' + type) }) }}
    </h3>

    <p class="mb-7">
      {{ $t('enterCode.sentTo', { recipient }) }}
      <v-icon small color="darkBlue" @click="back">mdi-pencil</v-icon>
    </p>

    <div v-if="errorMessage" class="mb-4 darkRed--text">
      {{ errorMessage }}
    </div>

    <v-otp-input
      v-model="verificationCode"
      class="mb-5 code-input"
      plain
      length="6"
      inputmode="tel"
      @finish="verifyCode"
    />

    <p
      class="link mb-5"
      :class="{ 'link--disabled': cooldown > 0 }"
      @click="cooldown <= 0 && sendCode()"
    >
      {{
        cooldown > 0
          ? $t('enterCode.resendCode') + ` (${cooldown}s)`
          : $t('enterCode.resendCode')
      }}
    </p>

    <PrimaryButton class="mb-5" :click="verifyCode" :disabled="disable">
      {{ $t('common.verify') }}
    </PrimaryButton>

    <SecondaryButton
      :disabled="disable"
      class="mt-4 mb-2"
      @click="setStage('accountFound')"
    >
      {{ $t('login.signInDifferentWay') }}
    </SecondaryButton>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import PrimaryButton from '@/components/common/PrimaryButton'
import SecondaryButton from '@/components/common/SecondaryButton'

export default {
  components: {
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      disable: false,
      errorMessage: null,
      verificationCode: null,
      defaultCooldown: 30,
      cooldown: 30,
      cooldownTimer: null,
    }
  },
  computed: {
    ...mapState({
      stage: 'stage',
      type: 'codeType',
      user: 'user',
    }),
    ...mapGetters({
      recipient: 'codeRecipient',
      safetyAssistant: 'safetyAssistant',
    }),
  },
  watch: {
    stage(value) {
      if (value === 'enterCode' || value === 'enterTempCode') {
        this.errorMessage = null
      }
    },
  },
  mounted() {
    this.startCooldown()
  },
  beforeDestroy() {
    clearInterval(this.cooldownTimer)
  },
  methods: {
    startCooldown() {
      clearInterval(this.cooldownTimer)
      this.cooldown = this.defaultCooldown
      this.cooldownTimer = setInterval(() => {
        this.cooldown--
        if (this.cooldown <= 0) {
          clearInterval(this.cooldownTimer)
        }
      }, 1000)
    },
    ...mapActions({
      checkTempCode: 'checkTempCode',
      loginWithCode: 'loginWithCode',
      requestCode: 'requestCode',
      requestTempCode: 'requestTempCode',
      resetState: 'resetState',
      setStage: 'setStage',
    }),
    async sendCode() {
      if (this.cooldown > 0) return
      try {
        this.disable = true
        this.errorMessage = null
        if (this.stage === 'enterCode') {
          await this.requestCode({
            type: this.type,
            client_id: this.$route.query.client_id,
          })
        } else if (this.stage === 'enterTempCode') {
          await this.requestTempCode({
            identifier: this.recipient,
          })
        }
        this.startCooldown()
        this.disable = false
      } catch (err) {
        this.errorMessage =
          err.response && err.response.data && err.response.data.message
            ? err.response.data.message.toString()
            : this.$t('enterCode.errorSending')

        this.disable = false
      }
    },
    async verifyCode() {
      try {
        this.disable = true
        if (this.stage === 'enterCode') {
          await this.loginWithCode({ code: this.verificationCode })
        } else if (this.stage === 'enterTempCode') {
          await this.checkTempCode({ code: this.verificationCode })
        }
        this.disable = false
      } catch (err) {
        this.verificationCode = null
        this.errorMessage =
          err.response && err.response.data && err.response.data.message
            ? err.response.data.message.toString()
            : this.$t('enterCode.verifyError')

        this.disable = false
      }
    },
    back() {
      this.resetState()
    },
  },
}
</script>
<style scoped lang="sass">
.code
  color: $black
  font-size: 1rem
  font-weight: 500
.code-input
  background-color: $white
  border-radius: 5px
  border: 1px solid $darkBlue
  margin: auto
  padding: 2px 24px
  width: 250px
.heading
  align-items: center
  display: flex
  font-size: 1.25rem
  font-weight: 700
  place-content: center
.link--disabled
  opacity: 0.5
  pointer-events: none
  cursor: default

.theme--dark
  .heading
    color: $white !important
    .v-icon
      color: $white !important
  p
    color: $textMuted !important
  .darkRed--text
    color: $darkRed !important
  .link
    color: $darkBlue !important
  .code-input
    background-color: $darkBackground !important
    border-color: $darkBorder !important
    ::v-deep input
      color: $white !important
      caret-color: $white !important

  // Override OTP input underline colors
  ::v-deep .v-otp-input
    .v-input
      &:not(.v-input--has-state)
        .v-input__slot
          &:before
            border-color: $darkBorder !important
          &:hover:before
            border-color: $darkBorderHover !important
      &.v-input--is-focused
        .v-input__slot
          &:after
            border-color: $darkBlue !important

.safety-assistant
  .heading
    color: $white !important
    .v-icon
      color: $white !important
  p
    color: $textLight !important
  .darkRed--text
    color: $errorRed !important
  .link
    color: $primaryBlue !important
  .code-input
    background-color: $darkInputBg !important
    border-color: $darkBorder !important
    color: $white !important
    ::v-deep input
      color: $white !important
      caret-color: $white !important

  // Override OTP input underline colors
  ::v-deep .v-otp-input
    .v-input
      &:not(.v-input--has-state)
        .v-input__slot
          &:before
            border-color: $whiteOpacity42 !important
          &:hover:before
            border-color: $darkBorderFocus !important
      &.v-input--is-focused
        .v-input__slot
          &:after
            border-color: $primaryBlue !important
</style>
